import {
  Container,
  Geometry,
  Graphics,
  Mesh,
  Shader,
  Sprite,
  Texture,
  autoDetectRenderer,
  type Renderer,
} from 'pixi.js';
import type { Rarity } from '@/game/chests';
import { pedestalRadius, type ArrivalEvent, type ArrivalFrame } from './arrival-timeline';
import { ChestMesh, projectChestPoint, type ChestPose } from './chest-mesh';
import {
  chestLayoutFor,
  BASE_PITCH,
  BASE_YAW,
  restPose,
  revealPose,
  STILL_EMIT,
  STILL_FILL,
  STILL_LID,
  STILL_RIM,
  type ChestLayout,
  type Handoff,
} from './chest-pose';
import {
  CHARGE_OPEN_S,
  CHARGE_S,
  LAND_FALL_S,
  clamp01,
  easeOutCubic,
  type ChestEvent,
  type ChestTimeline,
} from './chest-timeline';
import { CONFETTI_COLOURS, PEDESTAL, RARITY_MATERIALS, type RarityMaterial } from './palette';
import type { Quality } from '@/pixi/quality';
import { destroyRenderer } from '@/pixi/pixi-destroy';

/** Blockari's tiers; 21 Wild has no ultra. */
type QualityTier = Quality | 'ultra';

/**
 * Blockari's chest scene (apps/client/src/render/chest-scene.ts), ported
 * as-is: the ground, the rays, the ring, the pedestals and beams of the
 * arrival, the particles. 21 Wild's changes are the palette (./palette.ts),
 * the pedestal's plum felt, and one layer on top — `cards`, where the reward
 * cards are drawn on 21 Wild's card stock (Blockari draws them in the DOM).
 *
 * Blockari's own description follows.
 *
 * The chest screen's Pixi layer: the rarity's ground (a radial gradient with
 * slow rays, a faint grid and the glow ring the chest stands on), the lit 3D
 * chest, its glows, and the particles — sparkles on an upgrade, the light
 * burst and confetti on the open. Everything reads the ChestTimeline; the
 * React screen owns the words, the cards and the buttons over it.
 *
 * Tier: the balanced tier (medium) and below halve the confetti and the
 * sparkles and cap the resolution at 2; the chest itself is one draw.
 */

const BG_VERT = /* glsl */ `
in vec2 aPosition;
out vec2 vPx;
uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform vec2 uRes;
void main() {
  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
  vPx = aPosition * uRes;
}
`;

const BG_FRAG = /* glsl */ `
precision highp float;
in vec2 vPx;
out vec4 finalColor;
uniform vec2 uRes;
uniform vec2 uCentre;
uniform vec3 uInner;
uniform vec3 uOuter;
uniform vec3 uRay;
uniform float uRayK;
uniform float uRayPhase;
uniform vec3 uRing;       // centre y, radius x, radius y (px)
uniform vec3 uRingColor;
uniform float uRingK;
uniform float uScale;     // px per unit (the chest size)
uniform float uAlpha;     // the arrival's veil over the page (1 = the reveal's full ground)

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

void main() {
  vec2 d = vPx - uCentre;
  float r = length(d) / max(uRes.x, uRes.y);
  // The ground: the rarity's colour at the chest, its deep tone at the edges.
  vec3 col = mix(uInner, uOuter, smoothstep(0.0, 0.75, r));
  // Rays from behind the chest, turning slowly, fading out with distance.
  float a = atan(d.y, d.x);
  float rays = 0.5 + 0.5 * cos(a * 14.0 + uRayPhase);
  rays = pow(rays, 3.0) * (0.55 + 0.45 * (0.5 + 0.5 * cos(a * 5.0 - uRayPhase * 0.6)));
  float fall = smoothstep(0.85, 0.05, r) * smoothstep(0.0, 0.08, r);
  col += uRay * rays * fall * uRayK;
  // The faint table grid (the board's language), strongest near the ring.
  vec2 g = abs(fract(vPx / (uScale * 0.16)) - 0.5);
  float grid = smoothstep(0.47, 0.5, max(g.x, g.y));
  col += uInner * grid * 0.05 * smoothstep(0.7, 0.1, r);
  // The glow ring on the floor: an elliptical band and its soft halo.
  vec2 e = vec2(d.x / uRing.y, (vPx.y - uRing.x) / uRing.z);
  float er = length(e);
  float band = exp(-pow((er - 1.0) * 9.0, 2.0));
  float halo = exp(-pow((er - 1.0) * 3.0, 2.0)) * 0.45;
  float pool = smoothstep(1.05, 0.0, er) * 0.35;
  col += uRingColor * (band * 1.1 + halo + pool) * uRingK;
  // A whisper of grain so the gradient never bands.
  col += (hash(vPx) - 0.5) * 0.012;
  finalColor = vec4(col * uAlpha, uAlpha);
}
`;

function rgb(c: number): [number, number, number] {
  return [((c >> 16) & 0xff) / 255, ((c >> 8) & 0xff) / 255, (c & 0xff) / 255];
}
function mix3(a: number, b: number, t: number): [number, number, number] {
  const x = rgb(a);
  const y = rgb(b);
  return [x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t];
}
function mixHex(a: number, b: number, t: number): number {
  const [r, g, bl] = mix3(a, b, t);
  return (Math.round(r * 255) << 16) | (Math.round(g * 255) << 8) | Math.round(bl * 255);
}

/** Canvas-drawn particle textures (once per scene). */
function canvasTexture(
  size: number,
  draw: (c: CanvasRenderingContext2D, s: number) => void,
): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) draw(ctx, size);
  return Texture.from(canvas);
}

function glowTexture(): Texture {
  return canvasTexture(256, (c, s) => {
    const g = c.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.25, 'rgba(255,255,255,0.55)');
    g.addColorStop(0.6, 'rgba(255,255,255,0.12)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, s, s);
  });
}

function starTexture(): Texture {
  return canvasTexture(128, (c, s) => {
    const h = s / 2;
    const g = c.createRadialGradient(h, h, 0, h, h, h * 0.35);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, s, s);
    c.fillStyle = 'rgba(255,255,255,0.95)';
    for (const rot of [0, Math.PI / 2]) {
      c.save();
      c.translate(h, h);
      c.rotate(rot);
      c.beginPath();
      c.moveTo(-h, 0);
      c.quadraticCurveTo(0, -h * 0.06, h, 0);
      c.quadraticCurveTo(0, h * 0.06, -h, 0);
      c.fill();
      c.restore();
    }
  });
}

function ringTexture(): Texture {
  return canvasTexture(256, (c, s) => {
    const h = s / 2;
    const g = c.createRadialGradient(h, h, h * 0.7, h, h, h);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.55, 'rgba(255,255,255,0.9)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, s, s);
  });
}

function rayTexture(): Texture {
  // A long soft wedge: the open chest's light shafts.
  return canvasTexture(256, (c, s) => {
    const g = c.createLinearGradient(0, s, 0, 0);
    g.addColorStop(0, 'rgba(255,255,255,0.9)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.beginPath();
    c.moveTo(s * 0.42, s);
    c.lineTo(s * 0.58, s);
    c.lineTo(s, 0);
    c.lineTo(0, 0);
    c.closePath();
    c.fill();
  });
}

/**
 * The arrival's beam of light: a column from the top edge to the pedestal,
 * a hot core in a soft halo that widens toward the foot, slow shafts
 * turning inside it and motes drifting down through it. One quad per beam,
 * additive. The low tier draws a flat gradient column instead (`beamTexture`).
 */
const BEAM_VERT = /* glsl */ `
in vec2 aPosition;
out vec2 vUv;
uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
void main() {
  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
  vUv = aPosition;
}
`;

const BEAM_FRAG = /* glsl */ `
precision highp float;
in vec2 vUv;
out vec4 finalColor;
uniform vec3 uColor;
uniform float uK;
uniform float uTime;
uniform float uAspect;   // quad height / width

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
}

void main() {
  float v = vUv.y;                       // 0 at the top edge, 1 at the foot
  float dx = (vUv.x - 0.5) * 2.0;        // -1..1 across the quad
  float spread = mix(0.42, 0.72, v);     // wider toward the foot
  float core = exp(-pow(dx / (spread * 0.38), 2.0));
  float halo = exp(-pow(dx / spread, 2.0));
  // Shafts: slow bands turning inside the column.
  float shafts = 0.72 + 0.28 * sin(dx * 19.0 + uTime * 1.1) * sin(dx * 6.0 - uTime * 0.6 + v * 2.0);
  // Motes: specks drifting down through the light.
  vec2 mp = vec2(vUv.x * 16.0, v * uAspect * 16.0 - uTime * 2.2);
  float motes = smoothstep(0.82, 0.96, noise(mp)) * halo * (0.6 + 0.4 * sin(uTime * 3.0 + mp.x));
  // Brighter toward the foot; a soft foot where it meets the pedestal.
  float along = 0.45 + 0.55 * smoothstep(0.0, 0.9, v);
  float foot = 1.0 - smoothstep(0.8, 1.0, v);
  // The quad is cut off at its sides: the halo reaches 0 before the edge, so no column's edge is ever drawn.
  foot *= 1.0 - smoothstep(0.72, 1.0, abs(dx));
  float a = (core * 0.9 + halo * 0.55) * shafts * along * foot + motes * 0.8 * foot;
  vec3 col = uColor * a + vec3(1.0, 0.98, 0.92) * core * 0.45 * along * foot;
  float k = uK;
  finalColor = vec4(col * k, clamp(a * k * 0.85, 0.0, 1.0));
}
`;

function beamTexture(): Texture {
  // The low tier's beam: a plain column, bright in the middle, fading at the sides, the top and the foot.
  return canvasTexture(128, (c, s) => {
    const g = c.createLinearGradient(0, 0, s, 0);
    g.addColorStop(0, 'rgba(255,255,255,0)');
    g.addColorStop(0.35, 'rgba(255,255,255,0.35)');
    g.addColorStop(0.5, 'rgba(255,255,255,0.9)');
    g.addColorStop(0.65, 'rgba(255,255,255,0.35)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    c.fillStyle = g;
    c.fillRect(0, 0, s, s);
    const v = c.createLinearGradient(0, 0, 0, s);
    v.addColorStop(0, 'rgba(0,0,0,1)');
    v.addColorStop(0.5, 'rgba(0,0,0,0)');
    c.globalCompositeOperation = 'destination-out';
    c.fillStyle = v;
    c.fillRect(0, 0, s, s);
    // And at the foot: on a phone the column is wider than the screen, so a hard bottom edge is a seam
    // across the whole width (the light above it, a flat band below). It melts into the floor instead.
    const f = c.createLinearGradient(0, s * 0.62, 0, s);
    f.addColorStop(0, 'rgba(0,0,0,0)');
    f.addColorStop(1, 'rgba(0,0,0,1)');
    c.fillStyle = f;
    c.fillRect(0, 0, s, s);
  });
}

interface Particle {
  sprite: Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  age: number;
  spin: number;
  gravity: number;
  drag: number;
  size: number;
  /** Confetti flutter: the sprite's y scale follows a turning phase. */
  flutter: number;
  kind: 'star' | 'confetti' | 'glow' | 'puff';
  alpha: number;
}

/** A tiny deterministic generator for particle scatter (the capture replays the same frames). */
class Scatter {
  private s: number;
  constructor(seed = 0x9e3779b9) {
    this.s = seed >>> 0;
  }
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  range(a: number, b: number): number {
    return a + (b - a) * this.next();
  }
}

export type { ChestLayout } from './chest-pose';

/** The veil's strength over the page at its height (the page reads through it only as a shadow). */
const VEIL = 0.99;

/** The arrival's part of a frame: what `ArrivalClock` says, and the chests' rarities. */
export interface SceneArrival {
  frame: ArrivalFrame;
  /** Each landed chest's starting rarity (the veil is the first's). */
  rarities: readonly Rarity[];
  events: readonly ArrivalEvent[];
}

/** One landed chest's furniture: its pedestal, beam, rings, seam light and contact shadow. */
interface Stand {
  pedestal: Graphics;
  pedGlow: Sprite;
  pedRing: Sprite;
  shadow: Sprite;
  beam: Mesh<Geometry, Shader> | null;
  beamShader: Shader | null;
  beamFlat: Sprite | null;
  dust: Sprite;
  shock: Sprite;
  seam: Sprite;
  seamCore: Sprite;
  /** The low tier's chest: the baked still. */
  still: Sprite | null;
}

export class ChestScene {
  renderer: Renderer | null = null;
  private readonly stage = new Container();
  private bg: Mesh<Geometry, Shader> | null = null;
  private bgShader: Shader | null = null;
  private chest: ChestMesh | null = null;
  /** The arrival's other chests (a batch of two or three lands side by side). */
  private readonly extra: ChestMesh[] = [];
  private readonly stands: Stand[] = [];
  private readonly back = new Container();
  private readonly chests = new Container();
  private readonly front = new Container();
  private readonly particles: Particle[] = [];
  private glowBack: Sprite | null = null;
  private burst: Sprite | null = null;
  private shock: Sprite | null = null;
  private readonly shafts: Sprite[] = [];
  private tex: {
    glow: Texture;
    star: Texture;
    ring: Texture;
    ray: Texture;
    beam: Texture;
    white: Texture;
  } | null = null;
  private layout: ChestLayout = { width: 1, height: 1, x: 0, y: 0, size: 100 };
  private rayPhase = 0;
  private readonly rng = new Scatter();
  private cap = 1;
  /** Reduced motion: the open throws nothing (the cards fade in on their own). */
  reduced = false;
  private shockAt = -10;
  private burstAt = -10;
  private pose: ChestPose | null = null;
  private sparkleClock = 0;
  private readonly stills = new Map<Rarity, Texture>();

  /**
   * `arrival`: how many chests the arrival lands (0 = the reveal alone). The
   * low tier's arrival draws the baked still (`stills`) in place of the mesh.
   */
  constructor(
    private readonly tier: QualityTier,
    private readonly arrival = 0,
  ) {
    this.cap = tier === 'ultra' || tier === 'high' ? 1 : 0.55;
  }

  /** The low tier: no volumetric beam, no sparkles, the chest a baked still while it arrives. */
  private get low(): boolean {
    return this.tier === 'low';
  }

  async init(canvasParent: HTMLElement, width: number, height: number): Promise<void> {
    const dpr = window.devicePixelRatio || 1;
    const maxRes = this.tier === 'low' ? 1 : this.tier === 'medium' ? 2 : 3;
    const renderer = await autoDetectRenderer({
      preference: 'webgl',
      width,
      height,
      resolution: Math.min(dpr, maxRes),
      autoDensity: true,
      antialias: true,
      // Transparent: the arrival's veil is the ground at partial alpha over the page (the
      // reveal's ground is opaque, alpha 1).
      backgroundAlpha: 0,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    });
    this.renderer = renderer;
    this.stage.eventMode = 'none';
    canvasParent.appendChild(renderer.canvas);
    this.tex = {
      glow: glowTexture(),
      star: starTexture(),
      ring: ringTexture(),
      ray: rayTexture(),
      beam: beamTexture(),
      white: Texture.WHITE,
    };

    const geometry = new Geometry({
      attributes: { aPosition: [0, 0, 1, 0, 1, 1, 0, 1] },
      indexBuffer: [0, 1, 2, 0, 2, 3],
    });
    this.bgShader = Shader.from({
      gl: { vertex: BG_VERT, fragment: BG_FRAG, name: 'w21-chest-ground' },
      resources: {
        groundUniforms: {
          uRes: { value: [1, 1], type: 'vec2<f32>' },
          uCentre: { value: [0, 0], type: 'vec2<f32>' },
          uInner: { value: [0, 0, 0], type: 'vec3<f32>' },
          uOuter: { value: [0, 0, 0], type: 'vec3<f32>' },
          uRay: { value: [1, 1, 1], type: 'vec3<f32>' },
          uRayK: { value: 0.2, type: 'f32' },
          uRayPhase: { value: 0, type: 'f32' },
          uRing: { value: [0, 100, 30], type: 'vec3<f32>' },
          uRingColor: { value: [1, 1, 1], type: 'vec3<f32>' },
          uRingK: { value: 1, type: 'f32' },
          uScale: { value: 100, type: 'f32' },
          uAlpha: { value: 1, type: 'f32' },
        },
      },
    });
    this.bg = new Mesh({ geometry, shader: this.bgShader });
    this.stage.addChild(this.bg);

    // Behind the chest: its glow and the open chest's shafts.
    this.glowBack = new Sprite(this.tex.glow);
    this.glowBack.anchor.set(0.5);
    this.glowBack.blendMode = 'add';
    this.back.addChild(this.glowBack);
    for (let i = 0; i < 7; i++) {
      const s = new Sprite(this.tex.ray);
      s.anchor.set(0.5, 1);
      s.blendMode = 'add';
      s.alpha = 0;
      this.shafts.push(s);
      this.back.addChild(s);
    }
    for (let i = 0; i < this.arrival; i++) this.stands.push(this.makeStand(geometry));
    this.stage.addChild(this.back);

    this.chest = new ChestMesh();
    this.chests.addChild(this.chest.mesh);
    for (let i = 1; i < this.arrival; i++) {
      const m = new ChestMesh();
      this.extra.push(m);
      this.chests.addChild(m.mesh);
    }
    for (const st of this.stands) if (st.still) this.chests.addChild(st.still);
    this.stage.addChild(this.chests);

    this.shock = new Sprite(this.tex.ring);
    this.shock.anchor.set(0.5);
    this.shock.blendMode = 'add';
    this.shock.alpha = 0;
    this.burst = new Sprite(this.tex.glow);
    this.burst.anchor.set(0.5);
    this.burst.blendMode = 'add';
    this.burst.alpha = 0;
    for (const st of this.stands) this.front.addChild(st.seam, st.seamCore, st.dust, st.shock);
    this.front.addChild(this.shock, this.burst);
    this.stage.addChild(this.front);

    this.resize(width, height);
    // Warm every path the reveal uses (sprite batch, the particle textures) before the clock runs:
    // a first upload mid-animation cost frames.
    this.fillPools();
    for (const kind of ['star', 'confetti', 'glow', 'puff'] as const) {
      const p = this.spawn(kind);
      if (p) {
        p.life = 0.05;
        p.x = -400;
        p.y = -400;
        p.sprite.position.set(-400, -400);
      }
    }
  }

  /** A pedestal, its light, its beam and its rings (made once, drawn every frame by transform). */
  private makeStand(quad: Geometry): Stand {
    const tex = this.tex!;
    // The pedestal: a low drum — dark stone sides, a lighter top face — drawn once in unit space
    // (radius 1, the top face's ellipse 0.3 deep), placed and scaled per frame.
    const pedestal = new Graphics();
    // (Drawn at radius 100 so the ellipses keep their segments when scaled.)
    pedestal
      .rect(-100, 0, 200, 20)
      .fill({ color: PEDESTAL.side })
      .ellipse(0, 20, 100, 30)
      .fill({ color: PEDESTAL.foot })
      .ellipse(0, 0, 100, 30)
      .fill({ color: PEDESTAL.top })
      .ellipse(0, 0, 86, 25)
      .fill({ color: PEDESTAL.inner });
    const pedGlow = new Sprite(tex.glow);
    pedGlow.anchor.set(0.5);
    pedGlow.blendMode = 'add';
    const pedRing = new Sprite(tex.ring);
    pedRing.anchor.set(0.5);
    pedRing.blendMode = 'add';
    const shadow = new Sprite(tex.glow);
    shadow.anchor.set(0.5);
    shadow.tint = 0x000000;
    let beam: Mesh<Geometry, Shader> | null = null;
    let beamShader: Shader | null = null;
    let beamFlat: Sprite | null = null;
    if (this.low) {
      beamFlat = new Sprite(tex.beam);
      beamFlat.anchor.set(0.5, 0);
      beamFlat.blendMode = 'add';
    } else {
      beamShader = Shader.from({
        gl: { vertex: BEAM_VERT, fragment: BEAM_FRAG, name: 'w21-chest-beam' },
        resources: {
          beamUniforms: {
            uColor: { value: [1, 1, 1], type: 'vec3<f32>' },
            uK: { value: 0, type: 'f32' },
            uTime: { value: 0, type: 'f32' },
            uAspect: { value: 4, type: 'f32' },
          },
        },
      });
      beam = new Mesh({ geometry: quad, shader: beamShader });
      beam.blendMode = 'add';
    }
    // Draw order behind the chest: the beam, the pedestal, its light, the shadow on it.
    if (beam) this.back.addChild(beam);
    if (beamFlat) this.back.addChild(beamFlat);
    this.back.addChild(pedestal, pedGlow, pedRing, shadow);
    const ring = (): Sprite => {
      const s = new Sprite(tex.ring);
      s.anchor.set(0.5);
      s.blendMode = 'add';
      s.alpha = 0;
      return s;
    };
    const dust = ring();
    dust.blendMode = 'normal';
    const shock = ring();
    const seam = new Sprite(tex.glow);
    seam.anchor.set(0.5);
    seam.blendMode = 'add';
    const seamCore = new Sprite(tex.glow);
    seamCore.anchor.set(0.5);
    seamCore.blendMode = 'add';
    let still: Sprite | null = null;
    if (this.low) {
      still = new Sprite(Texture.EMPTY);
      still.anchor.set(0.5);
    }
    return {
      pedestal,
      pedGlow,
      pedRing,
      shadow,
      beam,
      beamShader,
      beamFlat,
      dust,
      shock,
      seam,
      seamCore,
      still,
    };
  }

  /**
   * A still of the chest — the same mesh and material — for LATER's flyer and
   * the low tier's arrival: drawn once on this renderer (no second GL
   * context: creating one mid-arrival stalled a frame for ~300 ms), read back
   * into a PNG, in the same task as the next real frame so it never shows.
   * The chest sits centred, STILL_FILL of a `px` square, in the still's pose.
   */
  bakeStill(rarity: Rarity, px = 256): string | null {
    const r = this.renderer;
    if (!r) return null;
    const chest = new ChestMesh();
    const mat = RARITY_MATERIALS[rarity];
    chest.setMaterial(mat, mat, 1);
    chest.setPose({
      x: px / 2,
      y: px / 2,
      size: px * STILL_FILL,
      yaw: BASE_YAW,
      pitch: BASE_PITCH,
      roll: 0,
      sx: 1,
      sy: 1,
      lift: 0,
      lid: STILL_LID,
      flash: 0,
      rim: STILL_RIM,
      emit: STILL_EMIT,
    });
    const root = new Container();
    root.addChild(chest.mesh);
    r.render({ container: root, clearColor: [0, 0, 0, 0] });
    const res = r.resolution;
    const side = Math.round(px * res);
    const out = document.createElement('canvas');
    out.width = side;
    out.height = side;
    out
      .getContext('2d')
      ?.drawImage(r.canvas as HTMLCanvasElement, 0, 0, side, side, 0, 0, side, side);
    root.destroy({ children: true });
    const url = out.toDataURL('image/png');
    this.stills.set(rarity, Texture.from(out));
    return url;
  }

  /** Where the chest sits for a viewport: centred, a little above the middle (the cards go under it). */
  resize(width: number, height: number): void {
    this.layout = chestLayoutFor(width, height);
    this.renderer?.resize(width, height);
    if (this.bg) {
      this.bg.scale.set(width, height);
    }
  }

  get chestLayout(): ChestLayout {
    return this.layout;
  }

  /** The chest's mouth on screen (where the cards leave from), from the last pose. */
  mouth(): { x: number; y: number } {
    if (!this.pose) return { x: this.layout.x, y: this.layout.y };
    return projectChestPoint(this.pose, 0, 0.5, 0);
  }

  /** The main chest's pose on the last frame (the hand-over starts from it). */
  lastPose(): ChestPose | null {
    return this.pose ? { ...this.pose } : null;
  }

  /**
   * Apply one frame and draw: the reveal's timeline (null while the arrival
   * alone is on screen), the arrival's frame (null once it has handed over
   * and faded), and the hand-over (the reveal's chest blends in from the
   * arrival's last pose).
   */
  render(
    tl: ChestTimeline | null,
    dt: number,
    events: readonly ChestEvent[],
    arrival: SceneArrival | null = null,
    handoff: Handoff | null = null,
  ): void {
    const renderer = this.renderer;
    if (!renderer || !this.chest || !this.bgShader || !this.tex) return;
    const L = this.layout;
    const a = arrival?.frame ?? null;
    const startRarity: Rarity = tl?.start ?? arrival?.rarities[0] ?? 'common';
    const mat = RARITY_MATERIALS[tl ? tl.rarity : startRarity];
    const prev = RARITY_MATERIALS[tl ? tl.previous : startRarity];
    const k = tl ? easeOutCubic(tl.recolour) : 1;
    const t = tl ? tl.t : (a?.t ?? 0);

    if (tl) for (const e of events) this.onEvent(e, tl, mat);
    if (arrival) for (const e of arrival.events) this.onArrivalEvent(e, arrival);

    // The main chest: the reveal's (blended in from the arrival's on a hand-over), or the arrival's first.
    const first = a?.chests[0];
    const pose = tl ? revealPose(tl, L, handoff) : (first?.pose ?? restPose(L.x, L.y, L.size, t));
    this.pose = pose;
    this.chest.setMaterial(prev, mat, k);
    this.chest.setPose(pose);
    const lowStill = this.low && !tl && a !== null;
    this.chest.mesh.visible = (tl !== null || (first?.visible ?? true)) && !lowStill;
    this.chest.mesh.alpha = 1;
    this.extra.forEach((m, j) => {
      const c = a?.chests[j + 1];
      m.mesh.visible = Boolean(c?.visible) && !this.low;
      if (!c) return;
      const r = RARITY_MATERIALS[arrival?.rarities[j + 1] ?? startRarity];
      m.setMaterial(r, r, 1);
      m.setPose(c.pose);
    });

    // Ground: the reveal's, or the arrival's veil (the ground at part alpha, deeper, no ring),
    // flooding into the reveal's on TAP TO OPEN.
    const u = (this.bgShader.resources as { groundUniforms: { uniforms: Record<string, unknown> } })
      .groundUniforms.uniforms;
    const open = tl && tl.openAt !== null ? tl.t - tl.openAt : -1;
    this.rayPhase += dt * (open >= 0 ? 0.55 : 0.22);
    u['uRes'] = [L.width, L.height];
    u['uCentre'] = [L.x, L.y - L.size * 0.05];
    const inner = mix3(prev.groundInner, mat.groundInner, k);
    const outer = mix3(prev.groundOuter, mat.groundOuter, k);
    const flood = a ? (tl ? a.flood : 0) : 1;
    const veilInner = mix3(mat.groundOuter, mat.groundInner, 0.62);
    u['uInner'] = inner.map((c, i) => veilInner[i]! + (c - veilInner[i]!) * flood);
    u['uOuter'] = outer;
    u['uRay'] = mix3(prev.glow, mat.glow, k);
    const rayOpen = open >= 0 ? 0.32 + 0.35 * Math.exp(-open * 1.6) : 0;
    const floodFlash = a && tl ? 0.3 * Math.sin(Math.PI * a.flood) : 0;
    u['uRayK'] =
      (0.1 + rayOpen + (tl?.phase === 'upgrade' ? 0.18 * Math.exp(-tl.local * 4) : 0)) *
        (0.4 + 0.6 * flood) +
      floodFlash;
    u['uRayPhase'] = this.rayPhase;
    const ringY = L.y + L.size * 0.36;
    const ringPop =
      tl?.phase === 'land' ? easeOutCubic((tl.local - LAND_FALL_S) / 0.35) : a && tl ? a.flood : 1;
    u['uRing'] = [
      ringY,
      L.size * 0.78 * (0.6 + 0.4 * ringPop),
      L.size * 0.2 * (0.6 + 0.4 * ringPop),
    ];
    u['uRingColor'] = mix3(prev.glow, mat.glow, k);
    const pulse = 0.85 + 0.15 * Math.sin(t * 3.1);
    const ringIn = tl?.phase === 'land' ? clamp01((tl.local - LAND_FALL_S) / 0.2) : 1;
    u['uRingK'] = ringIn * pulse * flood;
    u['uScale'] = L.size;
    u['uAlpha'] = a ? (a.veil * VEIL + (1 - a.veil * VEIL) * (tl ? a.flood : 0)) * a.layer : 1;

    // Glow behind the chest.
    const glow = mixHex(prev.glow, mat.glow, k);
    const gb = this.glowBack!;
    gb.position.set(pose.x, pose.y);
    gb.tint = glow;
    const charge =
      tl?.phase === 'charge' ? clamp01(tl.local / (tl.opening ? CHARGE_OPEN_S : CHARGE_S)) : 0;
    gb.scale.set((pose.size * (1.7 + 0.5 * charge + (open >= 0 ? 0.7 : 0))) / 256);
    const landed = first ? (first.visible ? clamp01(first.pedestal) : 0) : 1;
    gb.alpha =
      (0.32 + 0.35 * charge + (open >= 0 ? 0.3 : 0)) *
      (a && !tl ? landed * a.layer * (first?.alpha ?? 1) : 1) *
      (first && !first.visible && !tl ? 0 : 1);

    // The open chest's shafts: fanned up out of the mouth, turning slowly.
    const m = this.mouth();
    for (let i = 0; i < this.shafts.length; i++) {
      const s = this.shafts[i]!;
      if (open < 0) {
        // Never quite 0: an invisible sprite is still drawn, so its texture is resident before the open.
        s.alpha = 0.001;
        continue;
      }
      const spread = (i - (this.shafts.length - 1) / 2) * 0.24;
      s.position.set(m.x, m.y + L.size * 0.05);
      s.rotation = spread + Math.sin(t * 0.7 + i * 1.7) * 0.05;
      const len = L.size * (1.5 + 0.35 * Math.sin(t * 1.3 + i));
      s.scale.set((L.size * 0.5) / 256, len / 256);
      s.tint = mixHex(0xffffff, mat.glow, 0.35);
      s.alpha = clamp01(open / 0.25) * (0.22 + 0.08 * Math.sin(t * 2 + i * 2.1));
    }

    // Shockwave (upgrade, the hand-over's flood) and burst (open).
    const sw = this.shock!;
    const su = (t - this.shockAt) / 0.55;
    if (su >= 0 && su <= 1) {
      sw.position.set(pose.x, pose.y);
      sw.scale.set((L.size * (0.6 + 2.6 * easeOutCubic(su))) / 256);
      sw.alpha = (1 - su) * 0.85;
      sw.tint = mixHex(0xffffff, mat.glow, 0.4);
    } else sw.alpha = 0.001;
    const bu = (t - this.burstAt) / 0.9;
    const bst = this.burst!;
    if (bu >= 0 && bu <= 1) {
      bst.position.set(m.x, m.y);
      bst.scale.set((L.size * (1 + 5 * easeOutCubic(bu))) / 256);
      bst.alpha = Math.pow(1 - bu, 1.6) * 0.95;
      bst.tint = mixHex(0xfff2d8, mat.glow, 0.25);
    } else bst.alpha = 0.001;

    this.drawStands(arrival, t, tl !== null, pose);
    if (a && !this.low) this.driftSparkles(a, arrival!.rarities, dt);
    this.stepParticles(dt);
    renderer.render({ container: this.stage });
  }

  /** The arrival's furniture per landed chest (all of it hidden once the arrival is gone). */
  private drawStands(
    arrival: SceneArrival | null,
    t: number,
    revealing: boolean,
    main: ChestPose,
  ): void {
    const a = arrival?.frame ?? null;
    this.stands.forEach((st, i) => {
      const c = a?.chests[i];
      const on = Boolean(c) && (a?.layer ?? 0) > 0;
      const hide = (sp: Container | null) => {
        if (sp) sp.visible = on;
      };
      [
        st.pedestal,
        st.pedGlow,
        st.pedRing,
        st.shadow,
        st.beam,
        st.beamFlat,
        st.dust,
        st.shock,
        st.seam,
        st.seamCore,
        st.still,
      ].forEach(hide);
      if (!c || !a || !on) return;
      const rarity = arrival!.rarities[i] ?? 'common';
      const mat = RARITY_MATERIALS[rarity];
      // In the reveal the first chest's pedestal stays under it (it glides with it to the middle).
      const slot =
        revealing && i === 0 ? { ...c.slot, x: main.x, y: main.y, size: main.size } : c.slot;
      const S = slot.size;
      const layer = a.layer;
      // Where the chest's base meets the pedestal's top face.
      const baseY = slot.y + S * 0.34;
      const R = S * pedestalRadius(this.arrival);
      const ped = st.pedestal;
      // The hit presses the drum down a little and flattens it, then it springs back.
      ped.position.set(slot.x, baseY + S * 0.035 * c.press);
      ped.scale.set(R / 100, (R / 100) * (1 - 0.12 * c.press));
      const pedIn = clamp01(c.pedestal / 0.3);
      // (Never quite 0: an undrawn object uploads on its first frame mid-arrival.)
      ped.alpha = Math.max(0.001, pedIn * layer * c.alpha);
      ped.tint = mixHex(0xffffff, mat.glow, 0.18);
      st.pedGlow.position.set(slot.x, baseY);
      st.pedGlow.scale.set((R * 2.6) / 256, (R * 0.9) / 256);
      st.pedGlow.tint = mat.glow;
      st.pedGlow.alpha = Math.max(0.001, c.pedestal * 0.75 * layer * c.alpha);
      st.pedRing.position.set(slot.x, baseY);
      st.pedRing.scale.set((R * 2.08) / 256, (R * 0.62) / 256);
      st.pedRing.tint = mixHex(0xffffff, mat.glow, 0.5);
      st.pedRing.alpha = Math.max(0.001, c.pedestal * 0.95 * layer * c.alpha);
      // The contact shadow: small and faint while the chest is high, full as it lands.
      const lift = Math.max(0, c.pose.lift);
      const near = 1 / (1 + lift * 1.6);
      st.shadow.position.set(c.pose.x, baseY + S * 0.01);
      st.shadow.scale.set((S * 1.3 * near) / 256, (S * 0.34 * near) / 256);
      st.shadow.alpha = Math.max(0.001, (c.visible ? 0.55 * near * near : 0) * layer * c.alpha);
      // The beam, from the top edge down to the pedestal.
      const foot = baseY + S * 0.05;
      const bw = S * 2.1;
      const bh = Math.max(1, foot * c.beamFoot);
      const beamK = c.beamK * layer;
      if (st.beam && st.beamShader) {
        st.beam.position.set(c.slot.x - bw / 2, 0);
        st.beam.scale.set(bw, bh);
        const bu = (
          st.beamShader.resources as { beamUniforms: { uniforms: Record<string, unknown> } }
        ).beamUniforms.uniforms;
        bu['uColor'] = mix3(0xffffff, mat.glow, 0.6);
        bu['uK'] = beamK;
        bu['uTime'] = t;
        bu['uAspect'] = bh / bw;
        // Drawn at zero strength on the warm-up frames (the program links before the clock runs),
        // then only while it shows: a full-height additive quad is not free.
        st.beam.visible = beamK > 0.002 || t < 0.02;
      }
      if (st.beamFlat) {
        st.beamFlat.position.set(c.slot.x, 0);
        st.beamFlat.scale.set(bw / 128, bh / 128);
        st.beamFlat.tint = mixHex(0xffffff, mat.glow, 0.6);
        st.beamFlat.alpha = beamK * 0.6;
        st.beamFlat.visible = beamK > 0.002;
      }
      // Landing: a dust ring along the floor and a shockwave ring, in the rarity's light.
      if (c.dust >= 0) {
        const e = easeOutCubic(c.dust);
        st.dust.position.set(c.slot.x, baseY + S * 0.02);
        st.dust.scale.set((S * (0.9 + 1.5 * e)) / 256, (S * (0.26 + 0.42 * e)) / 256);
        st.dust.tint = mixHex(0xd9cdb8, mat.glow, 0.2);
        st.dust.alpha = (1 - c.dust) * 0.75 * layer;
      } else st.dust.alpha = 0.001;
      if (c.shock >= 0) {
        const e = easeOutCubic(c.shock);
        st.shock.position.set(c.slot.x, baseY - S * 0.05);
        st.shock.scale.set((S * (0.8 + 2.6 * e)) / 256, (S * (0.5 + 1.3 * e)) / 256);
        st.shock.tint = mixHex(0xffffff, mat.glow, 0.45);
        st.shock.alpha = (1 - c.shock) ** 1.4 * 0.95 * layer;
      } else st.shock.alpha = 0.001;
      // Light out of the lid seam: a warm line across the front where the lid meets the body.
      const seamK = c.visible ? c.seam * layer * c.alpha : 0;
      const sp = projectChestPoint(c.pose, 0, 0.46, 0.33);
      st.seam.position.set(sp.x, sp.y);
      st.seam.scale.set((S * 1.5) / 256, (S * 0.34) / 256);
      st.seam.tint = mat.glow;
      st.seam.alpha = Math.max(0.001, Math.min(1, seamK * 0.8));
      st.seamCore.position.set(sp.x, sp.y);
      st.seamCore.scale.set((S * 0.95) / 256, (S * 0.07) / 256);
      st.seamCore.tint = mixHex(0xffffff, mat.glow, 0.3);
      st.seamCore.alpha = Math.max(0.001, Math.min(1, seamK * 0.9));
      // The low tier's chest: the still, moved by the pose's place, lift and squash.
      if (st.still) {
        const tex = this.stills.get(rarity);
        if (tex && st.still.texture !== tex) st.still.texture = tex;
        const w = c.pose.size / STILL_FILL;
        st.still.position.set(c.pose.x, c.pose.y - c.pose.lift * c.pose.size);
        st.still.width = w * c.pose.sx;
        st.still.height = w * c.pose.sy;
        st.still.visible = c.visible && !revealing && Boolean(tex);
        st.still.alpha = layer * c.alpha;
      }
    });
  }

  /** The idle's sparkles: a slow drift up out of each landed chest's light. */
  private driftSparkles(a: ArrivalFrame, rarities: readonly Rarity[], dt: number): void {
    if (!a.idle || a.layer < 1 || a.buttonsOut > 0) return;
    this.sparkleClock += dt;
    const every = 0.11 / Math.max(0.5, this.cap);
    while (this.sparkleClock >= every) {
      this.sparkleClock -= every;
      a.chests.forEach((c, i) => {
        if (!c.visible || c.alpha < 0.5) return;
        const p = this.spawn('star');
        if (!p) return;
        const S = c.slot.size;
        const mat = RARITY_MATERIALS[rarities[i] ?? 'common'];
        p.x = c.slot.x + this.rng.range(-0.55, 0.55) * S;
        p.y = c.slot.y + this.rng.range(-0.1, 0.35) * S;
        p.vx = this.rng.range(-0.08, 0.08) * S;
        p.vy = -this.rng.range(0.18, 0.42) * S;
        p.life = this.rng.range(1.1, 1.9);
        p.drag = 0.4;
        p.gravity = 0;
        p.size = S * this.rng.range(0.035, 0.075);
        p.spin = this.rng.range(-1.5, 1.5);
        p.sprite.tint = this.rng.next() < 0.4 ? 0xffffff : mixHex(0xffffff, mat.glow, 0.6);
      });
    }
  }

  private onArrivalEvent(e: ArrivalEvent, arrival: SceneArrival): void {
    const c = arrival.frame.chests[e.type === 'done' ? 0 : e.index];
    if (!c) return;
    const mat = RARITY_MATERIALS[arrival.rarities[e.type === 'done' ? 0 : e.index] ?? 'common'];
    const S = c.slot.size;
    switch (e.type) {
      case 'land': {
        // Dust kicked out along the floor, low and heavy; a spray of light off the pedestal.
        const baseY = c.slot.y + S * 0.34;
        const n = Math.round(22 * this.cap + 8);
        for (let i = 0; i < n; i++) {
          const p = this.spawn('puff');
          if (!p) break;
          const side = i % 2 === 0 ? 1 : -1;
          const a = this.rng.range(0, 0.5);
          const v = S * this.rng.range(0.9, 2.2);
          p.x = c.slot.x + side * S * this.rng.range(0.25, 0.5);
          p.y = baseY + this.rng.range(-0.02, 0.05) * S;
          p.vx = side * Math.cos(a) * v;
          p.vy = -Math.sin(a) * v * 0.45;
          p.life = this.rng.range(0.45, 0.8);
          p.drag = 4.2;
          p.gravity = S * 0.25;
          p.size = S * this.rng.range(0.16, 0.3);
          p.spin = 0;
          p.alpha = 0.5;
          p.sprite.tint = mixHex(0xcbbfa8, mat.glow, 0.12);
        }
        if (!this.low)
          this.sparkleBurst(c.slot.x, baseY - S * 0.1, Math.round(26 * this.cap + 6), mat, S);
        // Chips of stone knocked off the pedestal's rim: thrown up and out, tumbling, falling back.
        const chips = Math.round(10 * this.cap + 6);
        for (let i = 0; i < chips; i++) {
          const p = this.spawn('confetti');
          if (!p) break;
          const side = i % 2 === 0 ? 1 : -1;
          p.x = c.slot.x + side * S * this.rng.range(0.35, 0.62);
          p.y = baseY + this.rng.range(-0.02, 0.04) * S;
          p.vx = side * S * this.rng.range(0.5, 1.6);
          p.vy = -S * this.rng.range(1.1, 2.2);
          p.life = this.rng.range(0.5, 0.8);
          p.drag = 1.2;
          p.gravity = S * 6;
          p.size = S * this.rng.range(0.025, 0.05);
          p.spin = this.rng.range(-14, 14);
          p.flutter = 0;
          p.sprite.tint = mixHex(PEDESTAL.chips, mat.glow, this.rng.range(0.05, 0.3));
        }
        break;
      }
      default:
        break;
    }
  }

  /** The hand-over's flood: a shockwave in the rarity's colour from the chest. */
  flood(at: number): void {
    this.shockAt = at;
  }

  private sparkleBurst(x: number, y: number, n: number, mat: RarityMaterial, S: number): void {
    for (let i = 0; i < n; i++) {
      const p = this.spawn('star');
      if (!p) return;
      const a = this.rng.range(Math.PI * 1.05, Math.PI * 1.95);
      const v = S * this.rng.range(0.6, 1.5);
      p.x = x + this.rng.range(-0.4, 0.4) * S;
      p.y = y;
      p.vx = Math.cos(a) * v;
      p.vy = Math.sin(a) * v;
      p.life = this.rng.range(0.4, 0.75);
      p.drag = 3;
      p.gravity = S * 1.2;
      p.size = S * this.rng.range(0.05, 0.1);
      p.spin = this.rng.range(-4, 4);
      p.sprite.tint = this.rng.next() < 0.5 ? 0xffffff : mixHex(0xffffff, mat.glow, 0.55);
    }
  }

  private onEvent(e: ChestEvent, tl: ChestTimeline, mat: RarityMaterial): void {
    const L = this.layout;
    switch (e.type) {
      case 'land':
        break;
      case 'upgrade':
        this.shockAt = tl.t;
        this.sparkles(Math.round(46 * this.cap + 8), mat, L.size * 1.25);
        break;
      case 'open': {
        this.burstAt = tl.t;
        this.shockAt = tl.t;
        // Reduced motion: the open throws nothing (the cards fade in on their own).
        if (this.reduced) break;
        this.sparkles(Math.round(40 * this.cap + 10), mat, L.size * 1.6);
        this.confetti(Math.round(300 * this.cap));
        break;
      }
      default:
        break;
    }
  }

  /** Sprites allocated once per kind (nothing is created mid-reveal); a dead particle's goes back. */
  private readonly pools: Record<Particle['kind'], Sprite[]> = {
    star: [],
    confetti: [],
    glow: [],
    puff: [],
  };

  private fillPools(): void {
    if (!this.tex) return;
    const sizes: Record<Particle['kind'], number> = {
      star: Math.round(200 * this.cap) + 40 + (this.arrival > 0 ? 80 : 0),
      confetti: Math.round(420 * this.cap) + 40,
      glow: 8,
      puff: this.arrival > 0 ? 100 : 1,
    };
    for (const kind of ['star', 'confetti', 'glow', 'puff'] as const) {
      const tex =
        kind === 'star'
          ? this.tex.star
          : kind === 'glow' || kind === 'puff'
            ? this.tex.glow
            : this.tex.white;
      for (let i = 0; i < sizes[kind]; i++) {
        const sprite = new Sprite(tex);
        sprite.anchor.set(0.5);
        if (kind !== 'confetti' && kind !== 'puff') sprite.blendMode = 'add';
        sprite.visible = false;
        this.front.addChild(sprite);
        this.pools[kind].push(sprite);
      }
    }
  }

  private spawn(kind: Particle['kind'], delay = 0): Particle | null {
    const sprite = this.pools[kind].pop();
    if (!sprite) return null;
    sprite.visible = delay <= 0;
    sprite.alpha = 1;
    sprite.rotation = 0;
    const p: Particle = {
      sprite,
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      life: 1,
      age: -delay,
      spin: 0,
      gravity: 0,
      drag: 1,
      size: 10,
      flutter: 0,
      kind,
      alpha: 1,
    };
    this.particles.push(p);
    return p;
  }

  /** A little spray of sparks at a screen point (a card landing in its slot). */
  sparkleAt(x: number, y: number, n: number): void {
    const L = this.layout;
    for (let i = 0; i < n; i++) {
      const p = this.spawn('star');
      if (!p) return;
      const a = this.rng.range(0, Math.PI * 2);
      const v = L.size * this.rng.range(0.4, 1.1);
      p.x = x + Math.cos(a) * L.size * 0.08;
      p.y = y + Math.sin(a) * L.size * 0.1;
      p.vx = Math.cos(a) * v;
      p.vy = Math.sin(a) * v - L.size * 0.3;
      p.life = this.rng.range(0.35, 0.6);
      p.drag = 3;
      p.gravity = L.size * 0.8;
      p.size = L.size * this.rng.range(0.05, 0.1);
      p.spin = this.rng.range(-4, 4);
      p.sprite.tint = 0xfff4d0;
    }
  }

  private sparkles(n: number, mat: RarityMaterial, speed: number): void {
    const L = this.layout;
    for (let i = 0; i < n; i++) {
      const p = this.spawn('star');
      if (!p) return;
      const a = this.rng.range(0, Math.PI * 2);
      const v = speed * this.rng.range(0.35, 1);
      p.x = L.x + Math.cos(a) * L.size * 0.2;
      p.y = L.y + Math.sin(a) * L.size * 0.14;
      p.vx = Math.cos(a) * v;
      p.vy = Math.sin(a) * v - speed * 0.25;
      p.life = this.rng.range(0.5, 0.95);
      p.drag = 2.6;
      p.gravity = L.size * 0.6;
      p.size = L.size * this.rng.range(0.07, 0.16);
      p.spin = this.rng.range(-3, 3);
      p.sprite.tint = this.rng.next() < 0.5 ? 0xffffff : mixHex(0xffffff, mat.glow, 0.55);
    }
  }

  /**
   * The open's confetti: a fountain out of the chest thrown wide across the
   * screen, then a rain from the top edge that keeps falling while the
   * cards are dealt.
   */
  private confetti(n: number): void {
    const L = this.layout;
    const m = this.mouth();
    const colours = [...CONFETTI_COLOURS, 0xffffff];
    const fountain = Math.round(n * 0.55);
    for (let i = 0; i < n; i++) {
      const rain = i >= fountain;
      const p = this.spawn('confetti', rain ? this.rng.range(0.15, 1.6) : 0);
      if (!p) return;
      if (rain) {
        p.x = this.rng.range(0, L.width);
        p.y = -this.rng.range(10, 80);
        p.vx = this.rng.range(-0.3, 0.3) * L.size;
        p.vy = this.rng.range(0.3, 0.8) * L.size;
        p.life = this.rng.range(2.4, 3.6);
        p.drag = 1.2;
        p.gravity = L.size * 0.9;
      } else {
        const a = -Math.PI / 2 + this.rng.range(-1.45, 1.45);
        const v = L.size * this.rng.range(2.2, 6.2);
        p.x = m.x + this.rng.range(-0.3, 0.3) * L.size;
        p.y = m.y;
        p.vx = Math.cos(a) * v * 1.25;
        p.vy = Math.sin(a) * v;
        p.life = this.rng.range(2.2, 3.4);
        p.drag = 1.35;
        p.gravity = L.size * 1.7;
      }
      p.size = L.size * this.rng.range(0.042, 0.07);
      p.spin = this.rng.range(-9, 9);
      p.flutter = this.rng.range(0, Math.PI * 2);
      p.sprite.tint = colours[Math.floor(this.rng.next() * colours.length)] ?? 0xffffff;
    }
  }

  private stepParticles(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      p.age += dt;
      const s = p.sprite;
      if (p.age < 0) continue;
      if (p.age >= p.life) {
        s.visible = false;
        this.pools[p.kind].push(s);
        this.particles.splice(i, 1);
        continue;
      }
      s.visible = true;
      const damp = Math.exp(-p.drag * dt);
      p.vx *= damp;
      p.vy = p.vy * damp + p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      const u = p.age / p.life;
      s.position.set(p.x, p.y);
      s.rotation += p.spin * dt;
      if (p.kind === 'puff') {
        // Dust: swells as it slows, thins out.
        const sz = p.size * (0.5 + 0.9 * Math.sqrt(u));
        s.width = sz;
        s.height = sz * 0.55;
        s.alpha = p.alpha * (1 - u) * Math.min(1, u * 8);
      } else if (p.kind === 'confetti') {
        p.flutter += dt * 11;
        s.width = p.size;
        s.height = p.size * 0.6 * (0.2 + 0.8 * Math.abs(Math.cos(p.flutter)));
        s.alpha = u < 0.8 ? 1 : 1 - (u - 0.8) / 0.2;
      } else {
        const sz = p.size * (u < 0.2 ? u / 0.2 : 1 - (u - 0.2) * 0.6);
        s.width = sz;
        s.height = sz;
        s.alpha = u < 0.6 ? 1 : 1 - (u - 0.6) / 0.4;
      }
    }
  }

  destroy(): void {
    this.particles.length = 0;
    if (this.renderer) destroyRenderer(this.renderer);
    this.renderer = null;
  }
}

/** The CSS colour of a rarity's title (the shell's words over the scene). */
export function rarityTitleColor(r: Rarity): string {
  return `#${RARITY_MATERIALS[r].title.toString(16).padStart(6, '0')}`;
}
